import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, ArrowUpRight, Phone, MessageSquare, Star } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { Reveal, SectionLabel } from "@/components/Reveal";
import { useBusinessInfo } from "@/lib/useBusinessInfo";
import { fetchPublicSettings } from "@/lib/api";

const FALLBACK_CITIES = [
  { name: "Houston", featured: false },
  { name: "Katy", featured: false },
  { name: "Fulshear", featured: false },
  { name: "Richmond", featured: false },
  { name: "Rosenberg", featured: false },
  { name: "Sugar Land", featured: false },
  { name: "Galveston", featured: false },
  { name: "The Woodlands", featured: false },
];

export const ServiceArea = () => {
  const { t } = useLanguage();
  const biz = useBusinessInfo();
  const [cities, setCities] = useState<{ name: string; featured: boolean }[]>(FALLBACK_CITIES);

  useEffect(() => {
    fetchPublicSettings()
      .then((r) => {
        if (Array.isArray(r.data.service_areas) && r.data.service_areas.length) {
          setCities(
            r.data.service_areas.map((a: any) =>
              typeof a === "string" ? { name: a, featured: false } : a,
            ),
          );
        }
      })
      .catch(() => {});
  }, []);

  return (
    <section
      id="service-area"
      data-testid="service-area-section"
      className="relative overflow-hidden border-t border-white/10 bg-ink py-24 md:py-36"
    >
      <span
        aria-hidden="true"
        className="text-stroke-thin pointer-events-none absolute -bottom-6 left-0 select-none whitespace-nowrap font-display text-[13vw] font-black uppercase leading-none tracking-tight opacity-60 md:-bottom-10 md:text-[11vw]"
      >
        Houston
      </span>
      <div className="relative mx-auto max-w-[1600px] px-5 md:px-10">
        <div className="grid gap-12 md:grid-cols-12 md:gap-16">
          <div className="md:col-span-7">
            <Reveal>
              <SectionLabel text={t.serviceArea.label} testId="service-area-label" />
              <h2
                data-testid="service-area-heading"
                className="mt-6 font-display text-4xl font-black uppercase leading-[0.95] tracking-tight text-white md:text-6xl"
              >
                {t.serviceArea.heading}
              </h2>
              <p className="mt-8 max-w-xl text-base leading-relaxed text-neutral-400 md:text-lg">
                {t.serviceArea.text}
              </p>
            </Reveal>

            <Reveal delay={0.1}>
              <ul data-testid="service-area-cities" className="mt-10 flex flex-wrap gap-3">
                {cities.map((city) => (
                  <li
                    key={city.name}
                    data-testid={`service-area-city-${city.name.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`flex items-center gap-2 rounded-full border px-5 py-2.5 text-sm font-bold tracking-wide transition-colors duration-300 hover:border-gold/50 hover:text-gold ${
                      city.featured
                        ? "border-gold/50 bg-gold/10 text-gold"
                        : "border-white/15 bg-charcoal text-neutral-200"
                    }`}
                  >
                    {city.featured ? (
                      <Star className="h-3.5 w-3.5 fill-gold text-gold" />
                    ) : (
                      <MapPin className="h-3.5 w-3.5 text-gold" />
                    )}
                    {city.name}
                  </li>
                ))}
              </ul>
              <p
                data-testid="service-area-disclaimer"
                className="mt-6 max-w-xl text-xs leading-relaxed text-neutral-500"
              >
                {t.serviceArea.disclaimer}
              </p>
            </Reveal>
          </div>

          <div className="flex items-end md:col-span-5">
            <Reveal delay={0.15} className="w-full">
              <div className="w-full border border-white/10 bg-charcoal p-8 md:p-10">
                <MapPin className="h-8 w-8 text-gold" strokeWidth={1.5} />
                <p className="mt-6 text-sm leading-relaxed text-neutral-300 md:text-base">
                  {t.serviceArea.note}
                </p>
                <div className="mt-7 flex flex-wrap gap-3">
                  <a
                    data-testid="service-area-call-btn"
                    href={biz.phoneHref}
                    className="flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 text-xs font-bold uppercase tracking-[0.12em] text-white transition-colors duration-300 hover:border-gold hover:text-gold"
                  >
                    <Phone className="h-3.5 w-3.5" />
                    {t.contact.call} · {biz.phone}
                  </a>
                  <a
                    data-testid="service-area-text-btn"
                    href={biz.smsHref}
                    className="flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 text-xs font-bold uppercase tracking-[0.12em] text-white transition-colors duration-300 hover:border-gold hover:text-gold"
                  >
                    <MessageSquare className="h-3.5 w-3.5" />
                    {t.contact.text}
                  </a>
                  <Link
                    data-testid="service-area-cta"
                    to="/request-a-bid"
                    className="group inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-xs font-black uppercase tracking-[0.12em] text-ink transition-colors duration-300 hover:bg-golddark"
                  >
                    {t.serviceArea.cta}
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
